import { Box, Flex, useBreakpointValue, VStack, Image, Text, useDisclosure, Progress, HStack, IconButton, useColorModeValue } from "@chakra-ui/react";
import { ReactNode } from "react";
import React, { useCallback } from "react";
import { useLyricsTheme } from "../../context/LyricsThemeContext";
import ProgressBar from "../ProgressBar";
import LyricTimingControls from "../LyricTimingControls";
import LyricsSettingsModal from "../LyricsSettingsModal";
import { FaPlay, FaPause, FaStepForward, FaStepBackward } from 'react-icons/fa';
import { useMusicService } from '../../context/MusicServiceContext';
import SpotifyControls from '../SpotifyControls';
import SpotifyAttribution from '../SpotifyAttribution';

interface SideBySideLayoutProps {
  albumImageUrl?: string;
  lyricsContent: ReactNode;
  playbackState: any;
  progressMs: number;
  duration: number;
  startProgressTimer: (startTime: number, isPlaying: boolean) => void;
  setProgressMs: (time: number) => void;
  accentColor: string;
  lyricsDelay: number;
  setLyricsDelay: (delay: number) => void;
  layoutMode: 'split' | 'tesla';
  setLayoutMode: (mode: 'split' | 'tesla') => void;
  onSettingsOpen?: () => void;
}

const SideBySideLayout = ({
  albumImageUrl,
  lyricsContent,
  playbackState,
  progressMs,
  duration,
  startProgressTimer,
  setProgressMs,
  accentColor,
  lyricsDelay,
  setLyricsDelay,
  layoutMode,
  setLayoutMode,
  onSettingsOpen
}: SideBySideLayoutProps) => {
  const { colorMode, isLightMode, backgroundType, backgroundColor } = useLyricsTheme();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isSpotifySelected } = useMusicService();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Use our local onOpen function
  const handleSettingsClick = () => {
    console.log("SideBySideLayout: calling local onOpen function");
    onOpen();
  };

  const albumWidth = useBreakpointValue({ base: "100%", md: "45%", lg: "40%", xl: "35%" });
  const lyricsWidth = useBreakpointValue({ base: "100%", md: "55%", lg: "60%", xl: "65%" });

  const albumImage = playbackState?.item?.album?.images[0]?.url || albumImageUrl;
  
  const trackName = playbackState?.item?.name || 'No Track Playing';
  const artistName = playbackState?.item?.artists 
    ? playbackState.item.artists.map((artist: any) => artist.name).join(', ') 
    : 'Unknown Artist';

  return (
    <Box 
      className={`${isLightMode ? 'light-mode' : 'dark-mode'} split-layout`}
      zIndex={1}
      maxH="100vh"
      height="100vh"
    >
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        zIndex={2}
        h="calc(100vh - 70px)"
      >
        {/* Left section: Album + Controls */}
        <Flex 
          direction="column" 
          w={{ base: "100%", md: albumWidth }}
          h="100%"
          bg="rgba(0, 0, 0, 0.2)"
          borderRight={{ base: "none", md: "1px solid rgba(255, 255, 255, 0.1)" }}
          position="relative"
          overflow="hidden"
        >
          {/* Album container with proper spacing */}
          <Box 
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={{ base: 4, md: 5 }}
            position="relative"
          >
            <Box
              width="65%"
              position="relative"
              borderRadius="md"
              overflow="hidden"
              boxShadow="0 8px 24px rgba(0, 0, 0, 0.4)"
              mb={4}
              sx={{ 
                aspectRatio: "1/1",
                // Fallback for browsers that don't support aspect-ratio
                "& > img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }
              }}
            >
              {albumImage && (
                <img
                  src={albumImage}
                  alt="Album Cover"
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover"
                  }}
                />
              )}
            </Box>
            
            {/* Track and Artist Info */}
            <Box 
              width="100%" 
              textAlign="center" 
              px={4}
            >
              <Text 
                fontSize={{ base: "xl", md: "4xl" }} 
                fontWeight="bold" 
                color="var(--color-text-primary)"
                noOfLines={1}
                mb={1}
              >
                {trackName}
              </Text>
              <Text 
                fontSize={{ base: "md", md: "lg" }} 
                color="var(--color-text-primary)"
                noOfLines={1}
                fontWeight="600"
              >
                {artistName}
              </Text>
            </Box>
          </Box>
          {/* Controls section with consistent padding */}
          <VStack
            spacing={2}
            p={6}
            bg="var(--color-background-muted)"
            borderTop="1px solid rgba(255, 255, 255, 0.05)"
            width="100%"
          >
            {/* Progress bar */}
            <Box width="100%">
              <ProgressBar
                playbackState={playbackState}
                progressMs={progressMs}
                duration={duration}
                startProgressTimer={startProgressTimer}
                setProgressMs={setProgressMs}
                accentColor={accentColor}
                isFullWidth={false}
              />
            </Box>

            {/* Spotify Controls */}
            {isSpotifySelected && (
              <Box width="100%" display="flex" justifyContent="center">
                <SpotifyControls />
              </Box>
            )}
            
            {/* Timing controls */}
            <Box width="100%">
              <LyricTimingControls
                lyricsDelay={lyricsDelay}
                setLyricsDelay={setLyricsDelay}
                compact={false}
              />
            </Box>
          </VStack>
        </Flex>

        {/* Lyrics section */}
        <Box 
          w={{ base: "100%", md: lyricsWidth }} 
          h="100%"
          overflow="hidden"
          position="relative"
          className="lyrics-section"
          p={{ base: 3, md: 6 }}
        >
          {/* Directly clone the component with our handler, but log the handler first */}
          {(() => {
            return React.cloneElement(lyricsContent as React.ReactElement, { 
              onSettingsOpen: handleSettingsClick 
            });
          })()}
          
          {/* Spotify Attribution (only when Spotify is selected) */}
          {isSpotifySelected && playbackState && (
            <SpotifyAttribution playbackState={playbackState} />
          )}
        </Box>
      </Flex>
      {/* Add Settings Modal with our local disclosure state */}
      <LyricsSettingsModal 
        isOpen={isOpen} 
        onClose={onClose}
        layoutMode={layoutMode}
        setLayoutMode={setLayoutMode}
      />
    </Box>
  );
};

export default SideBySideLayout; 